import React, { useEffect, useState } from 'react';
import { CheckCircle, Gift, Award } from 'lucide-react';
import AppLogo from './AppLogo';
import { getCommunityById } from '../services/communitiesService';

const Dashboard = ({ user, userActions, userRewards, tierStats }) => {
  const [communityDisplay, setCommunityDisplay] = useState('');

  useEffect(() => {
    if (user?.communityLabel) {
      setCommunityDisplay(user.communityLabel);
      return;
    }
    if (!user?.communityId) {
      setCommunityDisplay('');
      return;
    }
    let cancelled = false;
    getCommunityById(user.communityId)
      .then((c) => {
        if (!cancelled) setCommunityDisplay(c?.name || '');
      })
      .catch(() => {
        if (!cancelled) setCommunityDisplay('');
      });
    return () => {
      cancelled = true;
    };
  }, [user?.communityId, user?.communityLabel]);

  return (
  <div className="space-y-6">
    <div className="bg-gradient-to-r from-green-500 to-blue-600 rounded-xl p-6 text-white">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Welcome back, {user.name}!</h2>
          <p className="text-green-100 mt-1">Keep up the great climate work!</p>
          {communityDisplay ? (
            <p className="text-sm text-green-50 mt-2 opacity-95">
              Your community: <span className="font-medium">{communityDisplay}</span>
            </p>
          ) : null}
        </div>
        <div className="text-center shrink-0">
          <div className="flex justify-center mb-1">
            <Award className="h-8 w-8 text-amber-200" />
          </div>
          <div className="text-lg font-bold leading-tight">{tierStats?.tierLabel ?? 'No tier'}</div>
          <div className="text-xs text-green-100">Last 7 days</div>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <div className="font-semibold text-lg">{userActions.length}</div>
            <div className="text-sm text-gray-600">Total actions</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Gift className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <div className="font-semibold text-lg">{userRewards.length}</div>
            <div className="text-sm text-gray-600">Rewards given</div>
          </div>
        </div>
      </div>
    </div>

    <div>
      <h3 className="text-lg font-semibold mb-3">Recent climate actions</h3>
      {userActions.length === 0 ? (
        <div className="bg-white rounded-xl p-6 text-center border">
          <AppLogo className="h-12 w-12 mx-auto mb-2 object-contain opacity-40" />
          <p className="text-gray-600">No actions yet. Start making a difference!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {userActions.slice(0, 3).map((action) => (
            <div key={action.id} className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{action.title}</div>
                <div className="text-sm text-gray-600">
                  {new Date(action.created_at).toLocaleDateString()}
                </div>
                {action.description && (
                  <p className="text-sm text-gray-700 mt-1 line-clamp-2">{action.description}</p>
                )}
              </div>
              {action.photo_url && (
                <img src={action.photo_url} alt="Action proof" className="w-10 h-10 rounded-lg object-cover" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
  );
};

export default Dashboard;
